import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  CommandDialog,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandSeparator,
} from "@/components/ui/command";
import {
  Package,
  Users,
  FileText,
  Droplet,
  QrCode,
  ClipboardCheck,
  LayoutDashboard,
  Activity,
} from "lucide-react";
import { pushRecent } from "./recents";
import { SAFETY_MODULES } from "./modules";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } });

const QUICK_NAV = [
  { label: "Safety Dashboard", icon: LayoutDashboard, path: "/safety", testId: "cmd-nav-dashboard" },
  ...SAFETY_MODULES.map((m) => ({ label: m.label, icon: m.icon, path: m.route, testId: `cmd-nav-${m.key}` })),
];

export default function SafetyCommandPalette({ open, onOpenChange }) {
  const navigate = useNavigate();
  const [pos, setPos] = useState([]);
  const [reports, setReports] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [batches, setBatches] = useState([]);

  const fetchAll = useCallback(async () => {
    try {
      const [poRes, repRes, custRes, batchRes] = await Promise.all([
        axios.get(`${API}/po/list`, auth()).catch(() => ({ data: [] })),
        axios.get(`${API}/production/reports`, auth()).catch(() => ({ data: [] })),
        axios.get(`${API}/customers`, auth()).catch(() => ({ data: [] })),
        axios.get(`${API}/powder/batches`, auth()).catch(() => ({ data: [] })),
      ]);
      setPos(poRes.data.slice(0, 30));
      setReports(repRes.data.slice(0, 30));
      setCustomers(custRes.data.slice(0, 30));
      setBatches(batchRes.data.slice(0, 30));
    } catch (e) { /* silent */ }
  }, []);

  useEffect(() => {
    if (open) fetchAll();
  }, [open, fetchAll]);

  const go = (path, recent) => {
    onOpenChange(false);
    if (recent) pushRecent(recent);
    navigate(path);
  };

  return (
    <CommandDialog open={open} onOpenChange={onOpenChange}>
      <CommandInput placeholder="Search POs, Reports, Customers, Batches…  press Esc to close" data-testid="cmd-palette-input" />
      <CommandList>
        <CommandEmpty>No matches.</CommandEmpty>

        <CommandGroup heading="Quick Navigation">
          {QUICK_NAV.map((n) => (
            <CommandItem key={n.path} onSelect={() => go(n.path)} data-testid={n.testId}>
              <n.icon className="w-4 h-4 mr-2" />
              {n.label}
            </CommandItem>
          ))}
        </CommandGroup>

        {pos.length > 0 && (
          <>
            <CommandSeparator />
            <CommandGroup heading="Purchase Orders">
              {pos.map((p) => (
                <CommandItem
                  key={p.id}
                  value={`po ${p.po_number} ${p.customer_name || ""}`}
                  onSelect={() => go(`/safety/po/${p.id}/detail`, {
                    type: "po", id: p.id, label: `PO ${p.po_number}`, sublabel: p.customer_name || "",
                    path: `/safety/po/${p.id}/detail`,
                  })}
                  data-testid={`cmd-po-${p.po_number}`}
                >
                  <Package className="w-4 h-4 mr-2" />
                  <span className="font-mono">{p.po_number}</span>
                  <span className="text-xs text-gray-500 ml-2">{p.customer_name || "—"}</span>
                  <span className="ml-auto text-xs text-gray-400">{p.status}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </>
        )}

        {reports.length > 0 && (
          <>
            <CommandSeparator />
            <CommandGroup heading="Production Reports">
              {reports.map((r) => (
                <CommandItem
                  key={r.id}
                  value={`report ${r.id} ${r.report_date} ${(r.po_numbers || []).join(" ")}`}
                  onSelect={() => go(`/safety/production-reports/${r.id}`, {
                    type: "report", id: r.id, label: `Report ${r.report_date}`,
                    sublabel: (r.po_numbers || []).join(", ") || "",
                    path: `/safety/production-reports/${r.id}`,
                  })}
                  data-testid={`cmd-report-${r.id}`}
                >
                  <FileText className="w-4 h-4 mr-2" />
                  <span className="font-mono">{r.report_date}</span>
                  <span className="text-xs text-gray-500 ml-2">{(r.po_numbers || []).join(", ")}</span>
                  <span className="ml-auto text-xs text-gray-400">{r.status}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </>
        )}

        {customers.length > 0 && (
          <>
            <CommandSeparator />
            <CommandGroup heading="Customers">
              {customers.map((c) => (
                <CommandItem
                  key={c.id}
                  value={`customer ${c.name}`}
                  onSelect={() => go(`/safety/customers?focus=${c.id}`, {
                    type: "customer", id: c.id, label: c.name, sublabel: "",
                    path: `/safety/customers?focus=${c.id}`,
                  })}
                  data-testid={`cmd-customer-${c.id}`}
                >
                  <Users className="w-4 h-4 mr-2" />
                  {c.name}
                </CommandItem>
              ))}
            </CommandGroup>
          </>
        )}

        {batches.length > 0 && (
          <>
            <CommandSeparator />
            <CommandGroup heading="Powder Batches">
              {batches.map((b) => (
                <CommandItem
                  key={b.id}
                  value={`batch ${b.batch_no} ${b.shade_name || ""} ${b.make_name || ""}`}
                  onSelect={() => go(`/safety/powder-masters?focus=${b.id}`, {
                    type: "batch", id: b.id, label: `Batch ${b.batch_no}`,
                    sublabel: `${b.shade_name || ""} / ${b.make_name || ""}`,
                    path: `/safety/powder-masters?focus=${b.id}`,
                  })}
                  data-testid={`cmd-batch-${b.batch_no}`}
                >
                  <Droplet className="w-4 h-4 mr-2" />
                  <span className="font-mono">{b.batch_no}</span>
                  <span className="text-xs text-gray-500 ml-2">{b.shade_name} / {b.make_name}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </>
        )}
      </CommandList>
    </CommandDialog>
  );
}
